import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Save,
  Download,
  Eye,
  Settings,
  Layout,
  Sparkles,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { PresentationViewer } from '@/components/presentation/PresentationViewer';
import { usePresentationGenerator } from '@/hooks/usePresentationGenerator';
import { ExportDialog } from '@/components/presentation/export/ExportDialog';
import { ExportProgress } from '@/components/presentation/export/ExportProgress';
import type { ExportOptions, ExportResult } from '@/components/presentation/export/types';
import type { SlideData, PresentationData, SlideLayout } from '@/types/presentation';
import { apiPost, apiPut } from '@/lib/api/client';
import './PresentationBuilder.css';

interface SlideEditorProps {
  slide: SlideData;
  index: number;
  onChange: (index: number, updates: Partial<SlideData>) => void;
}

const SlideEditor = ({ slide, index, onChange }: SlideEditorProps) => {
  const handleChange = (field: keyof SlideData, value: string | number | boolean) => {
    onChange(index, { [field]: value });
  };

  return (
    <div className="space-y-4 p-4">
      <div>
        <Label htmlFor={`slide-title-${index}`}>Slide Title</Label>
        <Input
          id={`slide-title-${index}`}
          value={slide.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="Enter slide title"
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor={`slide-subtitle-${index}`}>Subtitle</Label>
        <Input
          id={`slide-subtitle-${index}`}
          value={slide.subtitle || ''}
          onChange={(e) => handleChange('subtitle', e.target.value)}
          placeholder="Enter subtitle (optional)"
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor={`slide-content-${index}`}>Content</Label>
        <Textarea
          id={`slide-content-${index}`}
          value={typeof slide.content === 'string' ? slide.content : JSON.stringify(slide.content, null, 2)}
          onChange={(e) => handleChange('content', e.target.value)}
          placeholder="Enter slide content (can be text or JSON for complex layouts)"
          rows={6}
          className="mt-1 font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground mt-1">
          For complex layouts, use JSON format. Example: {`{{"items":[{"title":"Item 1","description":"Description"}]}}`}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`slide-layout-${index}`}>Layout</Label>
          <Select
            value={slide.layout || 'center'}
            onValueChange={(value) => handleChange('layout', value as SlideLayout)}
          >
            <SelectTrigger id={`slide-layout-${index}`} className="mt-1">
              <SelectValue placeholder="Select layout" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="center">Center Content</SelectItem>
              <SelectItem value="left">Left Content</SelectItem>
              <SelectItem value="right">Right Content</SelectItem>
              <SelectItem value="top">Top Content</SelectItem>
              <SelectItem value="bottom">Bottom Content</SelectItem>
              <SelectItem value="full">Full Screen</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor={`slide-background-${index}`}>Background</Label>
          <Select
            value={slide.background || 'gradient-primary'}
            onValueChange={(value) => handleChange('background', value)}
          >
            <SelectTrigger id={`slide-background-${index}`} className="mt-1">
              <SelectValue placeholder="Select background" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="gradient-primary">Primary Gradient</SelectItem>
              <SelectItem value="gradient-secondary">Secondary Gradient</SelectItem>
              <SelectItem value="gradient-accent">Accent Gradient</SelectItem>
              <SelectItem value="gradient-dark">Dark Gradient</SelectItem>
              <SelectItem value="white">White</SelectItem>
              <SelectItem value="gray">Gray</SelectItem>
              <SelectItem value="dark">Dark</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor={`slide-notes-${index}`}>Presenter Notes</Label>
        <Textarea
          id={`slide-notes-${index}`}
          value={slide.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Enter presenter notes (optional)"
          rows={3}
          className="mt-1"
        />
      </div>
    </div>
  );
};

interface AIGenerationFormProps {
  onGenerate: (options: {
    topic: string;
    slideCount: number;
    style: 'professional' | 'casual' | 'creative' | 'minimalist';
    targetAudience: string;
    additionalContext: string;
  }) => void;
  isGenerating: boolean;
  progress: number;
  currentStage: string;
}

const AIGenerationForm = ({ onGenerate, isGenerating, progress, currentStage }: AIGenerationFormProps) => {
  const [topic, setTopic] = useState('');
  const [slideCount, setSlideCount] = useState(10);
  const [style, setStyle] = useState<'professional' | 'casual' | 'creative' | 'minimalist'>('professional');
  const [targetAudience, setTargetAudience] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!topic.trim()) return;

    onGenerate({
      topic: topic.trim(),
      slideCount,
      style,
      targetAudience,
      additionalContext,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      <div>
        <Label htmlFor="ai-topic">Topic *</Label>
        <Input
          id="ai-topic"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="e.g., Introduction to Machine Learning"
          disabled={isGenerating}
          className="mt-1"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ai-slide-count">Slide Count</Label>
          <Select value={slideCount.toString()} onValueChange={(v) => setSlideCount(parseInt(v))} disabled={isGenerating}>
            <SelectTrigger id="ai-slide-count" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[5, 8, 10, 12, 15, 20, 25, 30].map((count) => (
                <SelectItem key={count} value={count.toString()}>
                  {count} slides
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="ai-style">Style</Label>
          <Select value={style} onValueChange={(v: 'professional' | 'casual' | 'creative' | 'minimalist') => setStyle(v)} disabled={isGenerating}>
            <SelectTrigger id="ai-style" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="casual">Casual</SelectItem>
              <SelectItem value="creative">Creative</SelectItem>
              <SelectItem value="minimalist">Minimalist</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="ai-audience">Target Audience (Optional)</Label>
        <Input
          id="ai-audience"
          value={targetAudience}
          onChange={(e) => setTargetAudience(e.target.value)}
          placeholder="e.g., Software engineers, business executives"
          disabled={isGenerating}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="ai-context">Additional Context (Optional)</Label>
        <Textarea
          id="ai-context"
          value={additionalContext}
          onChange={(e) => setAdditionalContext(e.target.value)}
          placeholder="Any specific requirements or focus areas..."
          rows={3}
          disabled={isGenerating}
          className="mt-1"
        />
      </div>

      {isGenerating && (
        <div className="space-y-2 p-4 bg-muted rounded-lg">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{currentStage}</span>
            <span className="text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
            <motion.div
              className="h-full bg-primary"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      )}

      <Button type="submit" disabled={isGenerating || !topic.trim()} className="w-full">
        {isGenerating ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4 mr-2" />
            Generate with AI
          </>
        )}
      </Button>
    </form>
  );
};

const PresentationBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const { state: generatorState, generatePresentation } = usePresentationGenerator();

  const [presentation, setPresentation] = useState<PresentationData>({
    metadata: {
      title: 'Untitled Presentation',
      subtitle: 'Created with securesight.ai',
      author: '',
      date: new Date().toISOString().split('T')[0],
      description: '',
    },
    slides: [
      {
        id: 1,
        layout: 'center',
        background: 'gradient-primary',
        title: 'Welcome to Your Presentation',
        subtitle: 'Created with securesight.ai',
        content: '',
      },
    ],
  });

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'slides' | 'settings' | 'ai'>('ai');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportState, setExportState] = useState({
    isExporting: false,
    format: '' as 'pdf' | 'html' | 'pptx' | '',
    progress: 0,
    stage: '',
    error: null as string | null,
  });

  const currentSlide = presentation.slides[currentSlideIndex];

  const handleSlideChange = useCallback((index: number, updates: Partial<SlideData>) => {
    setPresentation((prev) => {
      const newSlides = [...prev.slides];
      newSlides[index] = { ...newSlides[index], ...updates };
      return { ...prev, slides: newSlides };
    });
  }, []);

  const handleAddSlide = useCallback(() => {
    const newSlide: SlideData = {
      id: presentation.slides.length + 1,
      layout: 'center',
      background: 'white',
      title: '',
      content: '',
    };

    setPresentation((prev) => ({
      ...prev,
      slides: [...prev.slides, newSlide],
    }));

    setCurrentSlideIndex(presentation.slides.length);
  }, [presentation.slides.length]);

  const handleRemoveSlide = useCallback((index: number) => {
    if (presentation.slides.length <= 1) {
      toast({
        title: 'Cannot Remove',
        description: 'Presentation must have at least one slide',
        variant: 'destructive',
      });
      return;
    }

    setPresentation((prev) => ({
      ...prev,
      slides: prev.slides.filter((_, i) => i !== index),
    }));

    if (currentSlideIndex >= presentation.slides.length - 1) {
      setCurrentSlideIndex(Math.max(0, presentation.slides.length - 2));
    }
  }, [presentation.slides.length, currentSlideIndex, toast]);

  const handleNavigateSlide = useCallback((direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentSlideIndex((prev) => Math.max(0, prev - 1));
    } else {
      setCurrentSlideIndex((prev) => Math.min(presentation.slides.length - 1, prev + 1));
    }
  }, [presentation.slides.length]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      // Prepare presentation data
      const presentationData = {
        title: presentation.metadata?.title || 'Untitled Presentation',
        description: presentation.metadata?.description || '',
        accessLevel: 'company' as const, // Default to company level for created presentations
        content: {
          slides: presentation.slides,
          metadata: presentation.metadata || {}
        }
      };

      let response: { presentation?: { id?: string } } | undefined;

      if (id) {
        // Update existing presentation
        response = await apiPut(`/api/presentations/${id}`, presentationData);
      } else {
        // Create new presentation
        response = await apiPost('/api/presentations', presentationData);
        // Navigate to the edit page with the new ID
        if (response?.presentation?.id) {
          navigate(`/presentations/${response.presentation.id}/edit`, { replace: true });
        }
      }

      toast({
        title: 'Presentation Saved',
        description: id
          ? 'Your presentation has been updated successfully'
          : 'Your presentation has been created successfully',
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error
        ? error.message
        : 'Could not save presentation. Please try again.';

      toast({
        title: 'Save Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }, [presentation, id, toast, navigate]);

  const handleExport = useCallback(async (options: ExportOptions): Promise<ExportResult> => {
    setExportState({
      isExporting: true,
      format: options.format,
      progress: 0,
      stage: 'Initializing...',
      error: null,
    });

    try {
      if (options.format === 'pdf') {
        setExportState(prev => ({ ...prev, stage: 'Preparing PDF export...' }));

        // Simulate progress for browser print
        setExportState(prev => ({ ...prev, progress: 30, stage: 'Opening print dialog...' }));
        await new Promise(resolve => setTimeout(resolve, 500));

        // Use browser's print to PDF functionality
        window.print();

        setExportState(prev => ({ ...prev, progress: 100, stage: 'Complete!' }));
        return { success: true };
      } else if (options.format === 'html') {
        setExportState(prev => ({ ...prev, stage: 'Generating HTML...' }));
        await new Promise(resolve => setTimeout(resolve, 300));

        setExportState(prev => ({ ...prev, progress: 20, stage: 'Processing slides...' }));
        await new Promise(resolve => setTimeout(resolve, 300));

        // Dynamically import HTML export utility to avoid loading it upfront
        const { generateHTMLExport, downloadHTML } = await import('@/lib/presentation/export/html');

        setExportState(prev => ({ ...prev, progress: 50, stage: 'Creating standalone file...' }));
        const htmlContent = generateHTMLExport(presentation, {
          includeSlideNumbers: options.includeSlideNumbers,
          includePresenterNotes: options.includeNotes,
        });

        setExportState(prev => ({ ...prev, progress: 80, stage: 'Downloading file...' }));
        await new Promise(resolve => setTimeout(resolve, 200));

        const filename = `${presentation.metadata.title || 'presentation'}.html`.replace(/[^a-z0-9]/gi, '_');
        downloadHTML(htmlContent, filename);

        setExportState(prev => ({ ...prev, progress: 100, stage: 'Complete!' }));
        return { success: true };
      } else if (options.format === 'pptx') {
        setExportState(prev => ({ ...prev, error: 'PPTX export is only available for saved presentations from the library' }));
        return {
          success: false,
          error: 'PPTX export is only available for saved presentations from the library'
        };
      }

      return { success: false, error: 'Unsupported format' };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Export failed';
      setExportState(prev => ({ ...prev, error: errorMessage }));
      return { success: false, error: errorMessage };
    } finally {
      // Reset export state after a delay
      setTimeout(() => {
        setExportState(prev => ({
          ...prev,
          isExporting: false,
          progress: 0,
          stage: '',
          error: null,
        }));
      }, 3000);
    }
  }, [presentation]);

  const handleAIGenerate = useCallback(
    async (options: {
      topic: string;
      slideCount: number;
      style: 'professional' | 'casual' | 'creative' | 'minimalist';
      targetAudience: string;
      additionalContext: string;
    }) => {
      const generatedPresentation = await generatePresentation(options);

      if (generatedPresentation) {
        // Update the presentation with the generated one
        setPresentation(generatedPresentation);
        setCurrentSlideIndex(0);
        setActiveTab('slides'); // Switch to slides tab to show generated content
      }
    },
    [generatePresentation]
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              <h1 className="text-2xl font-bold">Presentation Builder</h1>
              <Input
                value={presentation.metadata.title}
                onChange={(e) =>
                  setPresentation((prev) => ({
                    ...prev,
                    metadata: { ...prev.metadata, title: e.target.value },
                  }))
                }
                className="max-w-md"
                placeholder="Presentation title"
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/presentations')}
              >
                <Settings className="h-4 w-4 mr-2" />
                Library
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsPreviewMode(!isPreviewMode)}
              >
                <Eye className="h-4 w-4 mr-2" />
                {isPreviewMode ? 'Edit' : 'Preview'}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExportDialog(true)}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>

              <Button
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-73px)]">
        {/* Left Panel - Slides List */}
        <aside className="w-64 border-r bg-card overflow-y-auto">
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold text-sm">Slides</h2>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleAddSlide}
                className="h-7 w-7 p-0"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {presentation.slides.length} slide{presentation.slides.length !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="p-2 space-y-1">
            <AnimatePresence>
              {presentation.slides.map((slide, index) => (
                <motion.div
                  key={slide.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className={`relative group cursor-pointer rounded-lg border-2 transition-colors ${
                    index === currentSlideIndex
                      ? 'border-primary bg-primary/5'
                      : 'border-transparent hover:border-muted-foreground/20 hover:bg-muted/50'
                  }`}
                  onClick={() => setCurrentSlideIndex(index)}
                >
                  <div className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {slide.title || `Slide ${index + 1}`}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {slide.layout || 'center'} layout
                        </p>
                      </div>

                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSlide(index);
                        }}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  </div>

                  {index === currentSlideIndex && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-lg" />
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </aside>

        {/* Center Panel - Preview or Editor */}
        <main className="flex-1 overflow-hidden">
          {isPreviewMode ? (
            <div className="h-full overflow-auto">
              <PresentationViewer presentation={presentation} />
            </div>
          ) : (
            <div className="h-full overflow-auto">
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'slides' | 'settings' | 'ai')} className="h-full flex flex-col">
                <div className="border-b bg-card">
                  <div className="container mx-auto px-4">
                    <TabsList className="h-12">
                      <TabsTrigger value="ai" className="gap-2">
                        <Sparkles className="h-4 w-4" />
                        AI Generate
                      </TabsTrigger>
                      <TabsTrigger value="slides" className="gap-2">
                        <Layout className="h-4 w-4" />
                        Slides
                      </TabsTrigger>
                      <TabsTrigger value="settings" className="gap-2">
                        <Settings className="h-4 w-4" />
                        Settings
                      </TabsTrigger>
                    </TabsList>
                  </div>
                </div>

                <div className="flex-1 overflow-auto">
                  <TabsContent value="ai" className="h-full m-0 p-0">
                    <div className="container mx-auto p-6">
                      <div className="max-w-2xl mx-auto">
                        <div className="mb-4">
                          <h2 className="text-lg font-semibold">Generate with AI</h2>
                          <p className="text-sm text-muted-foreground mt-1">
                            Use AI to create a complete presentation in seconds. Just describe your topic and let AI do the rest.
                          </p>
                        </div>

                        <div className="bg-card rounded-lg border">
                          <AIGenerationForm
                            onGenerate={handleAIGenerate}
                            isGenerating={generatorState.isGenerating}
                            progress={generatorState.progress}
                            currentStage={generatorState.currentStage}
                          />
                        </div>

                        {generatorState.error && (
                          <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                            <p className="text-sm text-destructive font-medium">Generation Error</p>
                            <p className="text-sm text-destructive/80 mt-1">{generatorState.error}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="slides" className="h-full m-0 p-0">
                    <div className="container mx-auto p-6">
                      <div className="max-w-4xl mx-auto">
                        <div className="flex items-center justify-between mb-4">
                          <h2 className="text-lg font-semibold">
                            Edit Slide {currentSlideIndex + 1}
                          </h2>

                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleNavigateSlide('prev')}
                              disabled={currentSlideIndex === 0}
                            >
                              <ChevronLeft className="h-4 w-4 mr-1" />
                              Previous
                            </Button>

                            <span className="text-sm text-muted-foreground">
                              {currentSlideIndex + 1} / {presentation.slides.length}
                            </span>

                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleNavigateSlide('next')}
                              disabled={currentSlideIndex === presentation.slides.length - 1}
                            >
                              Next
                              <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </div>
                        </div>

                        <div className="bg-card rounded-lg border">
                          <SlideEditor
                            slide={currentSlide}
                            index={currentSlideIndex}
                            onChange={handleSlideChange}
                          />
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="settings" className="h-full m-0 p-0">
                    <div className="container mx-auto p-6">
                      <div className="max-w-2xl mx-auto">
                        <h2 className="text-lg font-semibold mb-4">Presentation Settings</h2>

                        <div className="space-y-4 bg-card rounded-lg border p-4">
                          <div>
                            <Label htmlFor="presentation-title">Title</Label>
                            <Input
                              id="presentation-title"
                              value={presentation.metadata.title}
                              onChange={(e) =>
                                setPresentation((prev) => ({
                                  ...prev,
                                  metadata: { ...prev.metadata, title: e.target.value },
                                }))
                              }
                              className="mt-1"
                            />
                          </div>

                          <div>
                            <Label htmlFor="presentation-description">Description</Label>
                            <Textarea
                              id="presentation-description"
                              value={presentation.metadata.description || ''}
                              onChange={(e) =>
                                setPresentation((prev) => ({
                                  ...prev,
                                  metadata: { ...prev.metadata, description: e.target.value },
                                }))
                              }
                              rows={3}
                              className="mt-1"
                            />
                          </div>

                          <div>
                            <Label htmlFor="presentation-author">Author</Label>
                            <Input
                              id="presentation-author"
                              value={presentation.metadata.author || ''}
                              onChange={(e) =>
                                setPresentation((prev) => ({
                                  ...prev,
                                  metadata: { ...prev.metadata, author: e.target.value },
                                }))
                              }
                              className="mt-1"
                            />
                          </div>

                          <div>
                            <Label htmlFor="presentation-date">Date</Label>
                            <Input
                              id="presentation-date"
                              type="date"
                              value={presentation.metadata.date || ''}
                              onChange={(e) =>
                                setPresentation((prev) => ({
                                  ...prev,
                                  metadata: { ...prev.metadata, date: e.target.value },
                                }))
                              }
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </div>
              </Tabs>
            </div>
          )}
        </main>
      </div>

      {/* Export Dialog */}
      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        onExport={handleExport}
        presentationTitle={presentation.metadata.title}
      />

      {/* Export Progress */}
      {exportState.isExporting && (
        <ExportProgress
          isExporting={exportState.isExporting}
          progress={exportState.progress}
          stage={exportState.stage}
          format={exportState.format}
          error={exportState.error}
        />
      )}
    </div>
  );
};

export default PresentationBuilder;
